// Checks on a recorded dose (docs/plans/phase2-emar-inpatient-pharmacy.md §4.3): a dose not
// given always has a reason; the time is between admission and now; "missed" only once the
// dose's time has come (TODO(clinical-verify) plan E11). Reasons are PHI-adjacent free text:
// the API never logs them.

import { err, ok, type Result } from '../result.ts';
import type { AdministrationOutcome, EmarWindows } from './timing.ts';

export const EMAR_REASON_MIN = 3;
export const EMAR_REASON_MAX = 500;
/** A sanity bound on the settings input, not a clinical threshold. */
export const EMAR_WINDOW_MAX_MINUTES = 1440;
/** Clock difference allowed between a device and the server. */
const CLOCK_SKEW_MS = 2 * 60_000;

/** Rule IDs of the alerts at the bedside, as logged in `clinical_alert_events`. */
export const EMAR_ALERT_RULES = {
  notVerified: 'emar.order_not_verified',
  prnMaximum: 'emar.prn_max_reached',
} as const;

export type AdministrationErrorCode =
  | 'emar.reason_required'
  | 'emar.reason_too_long'
  | 'emar.time_in_future'
  | 'emar.time_before_admission'
  | 'emar.missed_before_due';

export const ADMINISTRATION_ERRORS: Record<AdministrationErrorCode, string> = {
  'emar.reason_required': 'Say why the dose was not given',
  'emar.reason_too_long': `Keep the reason under ${String(EMAR_REASON_MAX)} characters`,
  'emar.time_in_future': 'The time is in the future. Check the time',
  'emar.time_before_admission': 'The time is before the patient was admitted. Check the time',
  'emar.missed_before_due': 'A dose can be recorded as missed only once its time has come',
};

export function checkAdministration(input: {
  outcome: AdministrationOutcome;
  reason: string | undefined;
  administeredAt: Date;
  admittedAt: Date;
  now: Date;
  scheduledFor: Date | null;
}): Result<{ reason: string | null }, AdministrationErrorCode> {
  const reason = (input.reason ?? '').trim();
  if (input.outcome !== 'given' && reason.length < EMAR_REASON_MIN) {
    return err('emar.reason_required');
  }
  if (reason.length > EMAR_REASON_MAX) return err('emar.reason_too_long');
  const at = input.administeredAt.getTime();
  if (at > input.now.getTime() + CLOCK_SKEW_MS) return err('emar.time_in_future');
  if (at < input.admittedAt.getTime()) return err('emar.time_before_admission');
  if (
    input.outcome === 'missed' &&
    input.scheduledFor !== null &&
    input.now.getTime() < input.scheduledFor.getTime()
  ) {
    return err('emar.missed_before_due');
  }
  return ok({ reason: reason === '' ? null : reason });
}

export function checkEmarWindows(windows: EmarWindows): Result<EmarWindows, 'emar.window_invalid'> {
  for (const value of [windows.earlyMinutes, windows.lateMinutes]) {
    if (value === null) continue;
    if (!Number.isInteger(value) || value < 0 || value > EMAR_WINDOW_MAX_MINUTES) {
      return err('emar.window_invalid');
    }
  }
  return ok(windows);
}
